import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';

import Tasks from 'APP/Tasks';
import { IChipsWithAvatarItem } from 'APP/components/ChipsWithAvatar/ChipsWithAvatar';
import { MIN_SEARCH_LENGTH } from 'APP/constants/search';
import { GroupType } from 'APP/model/group/groupModel.types';
import Entities from 'STORE';
import { User } from 'STORE/Users/User/User';

import { CHIPS_LIMIT } from '../SelectContacts.constants';
import {
  IUseSelectContactsPresenterProps,
  ISelectContactsCombinedPresenters,
  ISelectContactsPresenter,
} from '../SelectContacts.types';
import { useExtendedUsersPresenter } from './ExtendedUsers.presenter';
import { useMyContactsPresenter } from './MyContacts.presenter';

export const useSelectContactsPresenter = ({
  setSelectedIds,
  selectedContactsIds,
  targetGroupType,
  contacts,
  hasMore,
  loadMore,
  isLoading,
  groupId,
}: IUseSelectContactsPresenterProps): ISelectContactsCombinedPresenters => {
  const { t } = useTranslation();

  const myContactsPresenter = useMyContactsPresenter({
    contacts,
    hasMore,
    loadMore,
    isLoading,
  });

  const extendedUsersPresenter = useExtendedUsersPresenter({ groupId });

  const presenter = useLocalObservable<ISelectContactsPresenter>(() => ({
    searchValue: '',
    isChannel: [GroupType.ChannelOpen, GroupType.ChannelClosed].includes(targetGroupType),
    selectedContactsIds,

    get chips(): IChipsWithAvatarItem[] {
      return presenter.selectedContactsIds.map((id) => {
        const contact = Entities.usersStore.getUserById(id);

        return {
          id,
          avatarTitle: contact?.displayName || '',
          avatarUrl: contact?.avatarUrl || '',
          title: contact?.avatarTitle || contact?.nickName || '',
          avatarColorIndex: contact?.avatarColorIndex || '',
        };
      });
    },

    get isSearchMode(): boolean {
      return presenter.searchValue.length > MIN_SEARCH_LENGTH;
    },

    get canSelectMore(): boolean {
      return presenter.selectedContactsIds.length < CHIPS_LIMIT;
    },

    get noResults(): boolean {
      if (myContactsPresenter.isLoading) {
        return false;
      }

      if (presenter.isSearchMode && extendedUsersPresenter.isLoading) {
        return false;
      }

      if (presenter.isSearchMode) {
        return !extendedUsersPresenter.users.length;
      }

      return !myContactsPresenter.contacts.length;
    },

    toggleContact(candidate: User): void {
      if (
        (!presenter.isChannel && !candidate.canAddToGroupMe) ||
        (presenter.isChannel && !candidate.canAddToPublicMe)
      ) {
        Tasks.app.showAlert(t('privacy_settings_add_contact_alert'));
        return;
      }

      const isExist = presenter.selectedContactsIds.includes(candidate.id);

      if (isExist) {
        setSelectedIds(presenter.selectedContactsIds.filter((id: string) => id !== candidate.id));
      } else {
        if (!presenter.canSelectMore) {
          Tasks.app.showToast(t('common_group_alert_user_many_add'));
          return;
        }

        setSelectedIds([...presenter.selectedContactsIds, candidate.id]);
      }
    },

    getIsSelected(candidate: User): boolean {
      return presenter.selectedContactsIds.includes(candidate.id);
    },

    removeChips(item: IChipsWithAvatarItem): void {
      setSelectedIds(presenter.selectedContactsIds.filter((id: string) => id !== item.id));
    },

    onSearchValueChange(value: string): void {
      presenter.searchValue = value;

      extendedUsersPresenter.onSearchValueChange(value);
    },
  }));

  useEffect(() => {
    presenter.selectedContactsIds = selectedContactsIds;
  }, [selectedContactsIds]);

  return {
    presenter,
    myContactsPresenter,
    extendedUsersPresenter,
  };
};
